export interface ValidationErrors {
	[property: string]: string[];
}

export interface Validator<T> {
	(model: T): Promise<ValidationErrors>;
}

export interface ValidatorHandlers {
	[property: string]: (errors: string[]) => void;
}

export namespace ValidationErrors {

	export function add(errors: ValidationErrors, property: string, message: string) {
		const localErrors = errors[property] || [];
		localErrors.push(message);
		errors[property] = localErrors;
	}

	export function isValid(errors: ValidationErrors) {
		return Object.keys(errors).every(k => errors[k].length === 0);
	}

	export function concat(...errors: ValidationErrors[]): ValidationErrors {
		const result = {};
		errors.forEach(
			errors => Object.keys(errors).forEach(
				property => errors[property].forEach(errorMessage => ValidationErrors.add(result, property, errorMessage))
			));
		return result;
	}
}

export namespace Validator {

	export function create<T>(getValidationErrors: (model: T) => ValidationErrors | Promise<ValidationErrors>): Validator<T> {
		return model => Promise.resolve(getValidationErrors(model));
	}

	export function createWithCustomHandlers<T>(getValidationErrors: (model: T) => ValidationErrors, handlers: ValidatorHandlers): Validator<T> {
		return model => create(getValidationErrors)(model).then(errors => {

			const handlersPaths = Object.keys(handlers);
			let isInvalid = false;
			handlersPaths.forEach(path => {

				if (errors[path]) {
					handlers[path](errors[path]);
					delete errors[path];
					isInvalid = true;
				}
			});

			if (isInvalid)
				ValidationErrors.add(errors, 'validation is not valid', 'validation is not valid');

			return errors;
		});
	}

	export function createCustom<T>(getValidationErrors: (model: T) => ValidationErrors, handler: (errors: ValidationErrors) => void): Validator<T> {
		return (model: T) => create(getValidationErrors)(model).then(errors => {
			handler(errors);
			return ValidationErrors.isValid(errors) ? {} : { 'validation is not valid': ['validation is not valid'] } as any;
		});
	}
}
